import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { printHTML, downloadWord } from '../utils/printHelper';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, Storefront } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';
import { ULCINJ_GRB_DATA_URL } from '../assets/ulcinjGrb';

const VRSTE_OBJEKATA = [
  'Restoran / Restorani',
  'Picerija / Piceria',
  'Konoba / Konoba',
  'Grupa kafe-barova (kafana, kafić, kafeterija, disko-club-100m², noćni club-200m², bistro, pivnica, krčma i sl.) / Grupi i kafe bareve',
  'Catering objekat / Objekte kateringu',
  'Objekat brze hrane / Objekte të ushqimit të shpejtë',
  'Objekat jednostavnih usluga (poslastičarnica, pečenjara, kantina) / Objekte të shërbimeve të thjeshta',
];

const EMPTY = {
  vrsta_zahtjeva: 'ugostiteljska_opstina',
  client_id: '',
  datum_podnosenja: new Date().toISOString().split('T')[0],
  uo_povrsina: '',
  uo_ulica: '',
  uo_broj: '',
  uo_vrsta_objekta: 'Restoran',
  uo_naziv_podnosioca: '',
  uo_adresa_maticni: '',
  uo_naziv_objekta: '',
  uo_status_prostora: 'vlasnistvo',
  uo_period_djelatnosti: '',
  uo_ime_prezime_potpisnika: '',
  uo_vlasnik_zastupnik: '',
  uo_naziv_firme: '',
  uo_adresa_telefon: '',
};

export default function UgostiteljskaOpstinaPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=ugostiteljska_opstina'), api.get('/clients')]);
      setItems(z.data); setClients(c.data);
    } catch { toast.error('Greška'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pickClient = (cid) => {
    const c = clients.find(x => x.id === cid);
    if (!c) return;
    setForm(p => ({
      ...p, client_id: cid,
      uo_naziv_podnosioca: c.naziv_firme || c.skraceni_naziv || '',
      uo_adresa_maticni: `${c.adresa || ''}, ${c.grad || ''} — MB (PIB): ${c.pib || ''}`,
      uo_naziv_objekta: c.skraceni_naziv || '',
      uo_naziv_firme: c.naziv_firme || c.skraceni_naziv || '',
      uo_ime_prezime_potpisnika: c.direktor || '',
      uo_vlasnik_zastupnik: c.direktor || '',
      uo_adresa_telefon: `${c.adresa || ''}, ${c.grad || ''} — tel: ${c.telefon || ''}`,
    }));
  };

  const save = async () => {
    if (!form.uo_naziv_podnosioca) { toast.error('Unesite podnosioca'); return; }
    try {
      if (editingId) await api.put(`/zahtjevi/${editingId}`, form);
      else await api.post('/zahtjevi', form);
      toast.success('Sačuvano');
      setOpen(false); setEditingId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };
  const edit = (z) => { setEditingId(z.id); setForm({ ...EMPTY, ...z }); setOpen(true); };
  const del = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const dd = z.datum_podnosenja ? String(new Date(z.datum_podnosenja).getDate()).padStart(2, '0') : '____';
    const mm = z.datum_podnosenja ? String(new Date(z.datum_podnosenja).getMonth() + 1).padStart(2, '0') : '____';
    const yyyy = z.datum_podnosenja ? new Date(z.datum_podnosenja).getFullYear() : new Date().getFullYear();

    // Render vrste objekata — selected is bold+underlined
    const vrste = VRSTE_OBJEKATA.map(v => {
      const active = (z.uo_vrsta_objekta || '') === v;
      return active
        ? `<li style="font-weight:bold; text-decoration: underline;">${v}</li>`
        : `<li>${v}</li>`;
    }).join('');

    const statusLabel = {
      vlasnistvo: 'vlasništvu/pronësi',
      zakup: 'zakupu/qira',
      podzakup: 'pod zakupu/nën qira',
      porodica: 'član porodičnog domaćinstva/anëtarë i familjes',
    };
    const status = statusLabel[z.uo_status_prostora] || 'vlasništvu/pronësi';

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Zahtjev — Ugostiteljska — Opština Ulcinj</title>
<style>
@page { size: A4; margin: 10mm 14mm; }
* { margin:0; padding:0; box-sizing:border-box; font-family: 'Times New Roman', Times, serif; }
body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; color: #000; line-height: 1.35; }
.head-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.head-left { display: flex; align-items: flex-start; gap: 8px; flex: 1; }
.coa { width: 60px; height: 70px; flex: 0 0 60px; display: flex; align-items: center; justify-content: center; }
.coa img { max-width: 100%; max-height: 100%; }
.head-text { flex: 1; }
.head-text .cg { font-weight: bold; font-size: 11pt; }
.head-text .op { font-weight: bold; font-size: 11pt; margin-top: 1px; }
.head-text .sek { font-size: 10.5pt; margin-top: 4px; }
.head-text .sek-alb { font-size: 9.5pt; font-style: italic; color: #444; }
.head-text .br { margin-top: 4px; font-size: 10pt; }
.head-right { text-align: right; font-size: 9.5pt; line-height: 1.4; min-width: 150px; }
.sep { border-top: 1.2px solid #000; margin: 5px 0; }
.dept { text-align: center; font-weight: bold; text-transform: uppercase; margin: 18px 0 1px; font-size: 11pt; }
.dept-alb { text-align: center; font-style: italic; color: #444; font-size: 10pt; margin-bottom: 4px; }
.zahtjev-title { text-align: center; font-weight: bold; text-transform: uppercase; margin: 8px 0 3px; font-size: 14pt; letter-spacing: 1px; }
.zahtjev-sub { text-align: center; font-weight: bold; text-transform: uppercase; margin-bottom: 2px; font-size: 11.5pt; line-height: 1.25; }
.zahtjev-sub-alb { text-align: center; font-style: italic; color: #444; font-size: 10.5pt; margin-bottom: 8px; }
.alb { font-style: italic; color: #444; }
p { text-align: justify; margin: 3px 0; font-size: 11pt; }
.intro { margin: 4px 0 2px 0; font-size: 11pt; }
.intro-alb { font-style: italic; color: #444; font-size: 10pt; margin-bottom: 4px; }
.field { margin: 7px 0; font-size: 11pt; line-height: 1.4; }
.field-num { font-weight: bold; font-size: 11pt; }
.field-num-alb { font-style: italic; color: #444; font-weight: normal; font-size: 10pt; }
.line { border-bottom: 1px solid #000; padding: 1px 4px; display: block; min-height: 18px; font-size: 11pt; margin-top: 1px; }
.line-inline { border-bottom: 1px solid #000; padding: 0 4px; display: inline-block; flex: 1 1 220px; min-width: 100px; font-size: 11pt; vertical-align: bottom; }
.field-inline { display: flex; align-items: flex-end; gap: 6px; flex-wrap: wrap; }
.field-inline .field-num, .field-inline .field-num-alb { flex: 0 0 auto; }
.date-line { border-bottom: 1px solid #000; padding: 0 6px; display: inline-block; min-width: 130px; }
.obj-list { list-style: none; padding-left: 12px; margin: 3px 0; }
.obj-list li { margin: 2px 0; font-size: 10.5pt; }
.obj-list li:before { content: '• '; }
.opt-row-inline { display: flex; flex-wrap: wrap; gap: 14px; padding-left: 8px; margin-top: 3px; font-size: 10.5pt; }
.opt-row-inline > span { white-space: nowrap; }
.bold-active { font-weight: bold; text-decoration: underline; }
.bottom-wrap { display: flex; justify-content: space-between; margin-top: 50px; gap: 14px; }
.bottom-left { width: 40%; }
.bottom-right { width: 58%; }
.date-loc { font-size: 11pt; margin-top: 4px; }
.podnosilac-title { text-align: center; font-weight: bold; margin-bottom: 8px; font-size: 12pt; }
.sig-block { margin-bottom: 8px; }
.sig-line { border-bottom: 1px solid #000; min-height: 20px; padding: 2px 4px; font-size: 10.5pt; }
.sig-label { font-size: 9pt; text-align: center; line-height: 1.2; margin-top: 2px; }
.sig-label-alb { font-style: italic; color: #444; font-size: 8.5pt; }
.page-break { page-break-before: always; }
.doc-title { font-weight: bold; margin: 6px 0 8px; font-size: 12pt; }
.doc-title-alb { font-style: italic; color: #444; font-weight: normal; font-size: 10.5pt; }
.doc-list { padding-left: 22px; font-size: 10.5pt; line-height: 1.45; }
.doc-list > li { margin-bottom: 6px; }
.doc-alb { font-style: italic; color: #444; font-size: 10pt; display: block; }
.fee-list { padding-left: 14px; margin-top: 3px; }
.fee-list li { margin: 1px 0; }
.final-note { font-weight: bold; text-align: center; margin-top: 22px; font-size: 11pt; line-height: 1.4; }
.final-note-alb { font-style: italic; color: #444; font-weight: normal; font-size: 10pt; }
@media print { body { margin: 0; } }
</style></head><body>

<div class="head-row">
  <div class="head-left">
    <div class="coa"><img src="${ULCINJ_GRB_DATA_URL}" alt="Grb Opštine Ulcinj"/></div>
    <div class="head-text">
      <div class="cg">CRNA GORA / <span class="alb">MALI I ZI</span></div>
      <div class="op">Opština Ulcinj / <span class="alb">Komuna Ulqin</span></div>
      <div class="sek">Sekretarijat za privredu i ekonomski razvoj</div>
      <div class="sek-alb">Sekretariati për ekonomi dhe zhvillim ekonomik</div>
      <div class="br">Br./Nr.: 08-306<br>Ulcinj, Ulqin: ${dd}.${mm}.${yyyy}</div>
    </div>
  </div>
  <div class="head-right">
    Adresa: Bul. Đ. Kastrioti Skenderbeg<br>
    85360 Ulcinj, Crna Gora<br>
    tel. 412-206<br>
    www.ul-gov.me
  </div>
</div>
<div class="sep"></div>

<div class="dept">SEKRETARIJAT ZA PRIVREDU I EKONOMSKI RAZVOJ</div>
<div class="dept-alb">Sekretariati për ekonomi dhe zhvillim ekonomik</div>

<div class="zahtjev-title">ZAHTJEV / KËRKESË</div>
<div class="zahtjev-sub">ZA IZDAVANJE ODOBRENJA ZA OBAVLJANJE UGOSTITELJSKE DJELATNOSTI</div>
<div class="zahtjev-sub-alb">Për lëshimin e lejes për kryerjen e aktivitetit hotelier</div>

<p class="intro">- Obraćam se sa zahtjevom da mi izdate odobrenje za obavljanje ugostiteljske djelatnosti u ugostiteljskom objektu površine <b>${z.uo_povrsina || '__________'}</b> m², lociran (ulica) <b>${z.uo_ulica || '__________________________'}</b> u Ulcinju.</p>
<p class="intro-alb">Ju drejtohem me kërkesën për lëshimin e lejes për kryerjen e aktivitetit hotelier në objektin hotelier me sipërfaqe ${z.uo_povrsina || '__________'} m², i lokalizuar (rrugën) ${z.uo_ulica || '__________________________'} në Ulqin.</p>

<p style="font-weight:bold; margin-top:8px; font-size:10pt;">- Vrsta objekta (zaokružiti): / <span class="alb" style="font-weight:normal;">Lloji i objektit (rrumbullakso):</span></p>
<ul class="obj-list">${vrste}</ul>

<div class="field field-inline">
  <span class="field-num">1. Naziv odnosno ime i prezime podnosioca zahtjeva:</span><span class="field-num-alb">/Emri përkatësisht emri dhe mbiemri i parashtruesit të kërkesës:</span>
  <span class="line-inline">${z.uo_naziv_podnosioca || '&nbsp;'}</span>
</div>

<div class="field field-inline">
  <span class="field-num">2. Adresa i matični broj podnosioca zahtjeva:</span><span class="field-num-alb">/Adresa dhe numri i amzës i parashtruesit të kërkesës:</span>
  <span class="line-inline">${z.uo_adresa_maticni || '&nbsp;'}</span>
</div>

<div class="field field-inline">
  <span class="field-num">3. Objekat posluje pod nazivom</span><span class="field-num-alb">/Objekti kryen veprimtarinë me emrin:</span>
  <span class="line-inline">${z.uo_naziv_objekta || '&nbsp;'}</span>
</div>

<div class="field">
  <span class="field-num">4. Prostor je u (zaokruži)</span><span class="field-num-alb"> / hapësira është në (rrumbullakso):</span>
  <div class="opt-row-inline">
    <span class="${z.uo_status_prostora === 'vlasnistvo' ? 'bold-active' : ''}">• <b>vlasništvu</b>/<i>pronësi</i></span>
    <span class="${z.uo_status_prostora === 'zakup' ? 'bold-active' : ''}">• <b>zakupu</b>/<i>qira</i></span>
    <span class="${z.uo_status_prostora === 'podzakup' ? 'bold-active' : ''}">• <b>pod zakupu</b>/<i>nën qira</i></span>
    <span class="${z.uo_status_prostora === 'porodica' ? 'bold-active' : ''}">• <b>član porodičnog domaćinstva</b>/<i>anëtarë i familjes</i></span>
  </div>
</div>

<div class="field field-inline">
  <span class="field-num">5. Period obavljanja djelatnosti</span><span class="field-num-alb"> /Periudha e zhvillimit të veprimtarisë</span>
  <span class="line-inline">${z.uo_period_djelatnosti || '&nbsp;'}</span>
</div>

<div class="bottom-wrap">
  <div class="bottom-left">
    <div class="date-loc">U Ulcinju <span class="date-line">${dd}.${mm}.${yyyy}</span> god.</div>
  </div>
  <div class="bottom-right">
    <div class="podnosilac-title">PODNOSILAC ZAHTJEVA,<br><span class="alb" style="font-weight:normal;">Parashtruesi i kërkesës,</span></div>
    <div class="sig-block">
      <div class="sig-line">${z.uo_naziv_firme || '&nbsp;'}</div>
      <div class="sig-label"><b>Naziv privrednog društva, drugog pravnog lica i preduzetnika</b><br><span class="sig-label-alb">Emri i shoq.ekonomike, personit tjetër juridike dhe sipërmarrësit</span></div>
    </div>
    <div class="sig-block">
      <div class="sig-line">&nbsp;</div>
      <div class="sig-label"><b>Potpis odgovornog lica u priv.druš. i preduzetnika</b><br><span class="sig-label-alb">Nënshkrimi i personit përgjegjës në shoq.ekonomike dhe sipërmarrësit</span></div>
    </div>
    <div class="sig-block">
      <div class="sig-line">${z.uo_adresa_telefon || '&nbsp;'}</div>
      <div class="sig-label"><b>Adresa i telefon</b>/<span class="sig-label-alb">adresa dhe telefoni</span></div>
    </div>
  </div>
</div>

<div class="page-break"></div>

<p class="doc-title">Uz zahtjev prilažem sledeću dokumentaciju:<br><span class="doc-title-alb">Kërkesës i bashkangjes dokumentacionin e mëposhtëm:</span></p>

<ol class="doc-list">
  <li>Fotokopiju potvrde o registraciji u Centralni registar Privrednog suda (pribavlja se po službenoj dužnosti)
    <span class="doc-alb">Fotokopjen e vërtetimit të regjistrimit në Regjistrin qendror të Gjykatës ekonomike (sigurohet në mënyrë zyrtare)</span>
  </li>
  <li>Ovjerena fotokopija lične karte
    <span class="doc-alb">Fotokopjen e vërtetuar të letërnjoftimit</span>
  </li>
  <li>Akt o osnivanju društva (Statut ili Odluka)
    <span class="doc-alb">Akti i krijimit të shoqërisë (Statuti ose Vendimi)</span>
  </li>
  <li>Upotrebna dozvola ili rješenje za privremeni i pokretni ugostiteljski objekat, i prepis lista nepokretnosti (Uprava za nekretnine)
    <span class="doc-alb">Lejen e përdorimit ose aktvendimin për lokacionin e përkohshëm për objektin afarist dhe të lëvizshëm dhe transkripti i fletës poseduese (Administrata për patundëshmëri)</span>
  </li>
  <li>Upotrebna dozvola namjenska za disko club i noćni club — da je objekat građen u skladu sa propisima zaštite od buke
    <span class="doc-alb">Lejen e përdorimit për disko klub dhe klub nate — që objekti është i ndërtuar në përputhje me rregulloren e mbrojtjes nga zhurmat</span>
  </li>
  <li>Ugovor o zakupu ovjeren kod suda ili notara (ako je pod zakup)
    <span class="doc-alb">Kontrata e qirasë e vërtetuar në gjyq ose te notari (nëse është me qira)</span>
  </li>
  <li>Dokaz o završenom najmanje četvrtom nivou kvalifikacije podnivo IV 1 nacionalnog okvira kvalifikacije za poslovođu ili zaključen ugovor o radu za zaposlenog poslovođu i ovjeren u Upravu prihoda ekspozitura Ulcinj (obrazac JPR)
    <span class="doc-alb">Diplomën e nivelit të katërt të kvalifikimit nënniveli IV 1 të kornizës kombëtare të kualifikimit për udhëheqësin e objektit ose kontrata e punës për udhëheqësin e punësuar dhe aplikimi për regjistrimin e detyrueshëm tatimorë tek Drejtoria e tatimeve</span>
  </li>
  <li>Nalaz ovlašćene organizacije da objekat ispunjava uslove za obavljanje disko cluba i noćnog cluba shodno Zakona o zaštiti od buke
    <span class="doc-alb">Konstatimin e organizatës së autorizuar që objekti i plotëson kushtet për kryerjen e veprimtarisë hoteliere në pajtim me Ligjin për mbrojtje nga zhurma</span>
  </li>
  <li>Zapisnik o ispunjavanju minimalno-tehničkih uslova za obavljanje ugostiteljske djelatnosti (utvrđuje nadležni organ lokalne uprave)
    <span class="doc-alb">Procesverbalin për plotësimin e kushteve minimale teknike për veprimtarinë hoteliere</span>
  </li>
  <li>Popunjen obrazac za upis u Centralni turistički registar
    <span class="doc-alb">Formulari i plotësuar për regjistrim tek Regjistri Qendror i turizmit</span>
  </li>
  <li>Poresko uvjerenje (kancelarija 117), potvrdu za vodu i ugovor o odvozu smeća
    <span class="doc-alb">Vërtetimi i tatimit për patundëshmëri (zyra 117), vërtetimi për ujë dhe kontrata për transportin e mbetjeve</span>
  </li>
  <li>Dokaz — uplatnica o izvršenoj uplati za utvrđivanje minimalno tehničkih uslova na <b>ŽR 540-82567770-12 Opština Ulcinj</b> zavisno od površine objekta
    <span class="doc-alb">Dëshminë e pagesës së taksës administrative për plotësimin e kushteve minimale tehnike në varësi të kvadraturës së objektit në ZH.LL. 540-82567770-12 Buxheti i Komunës së Ulqinit</span>
    <ul class="fee-list">
      <li>a) Do 50 m²: <b>25,00 €</b> / <span class="alb">Deri 50 m²</span></li>
      <li>b) Od 50 do 200 m²: <b>35,00 €</b> / <span class="alb">Prej 50 deri 200 m²</span></li>
      <li>c) Preko 200 m²: <b>45,00 €</b> / <span class="alb">Përmbi 200 m²</span></li>
    </ul>
  </li>
  <li>Dokaz — uplatnica o izvršenoj uplati lokalne administrativne takse za izradu rješenja od <b>3,00 €</b> na <b>ŽR 540-82567770-12 Opština Ulcinj</b>
    <span class="doc-alb">Dëshminë e pagesës së taksës administrative në ZH.LL. 540-82567770-12 Buxheti i Komunës së Ulqinit në vlerën prej 3,00 €</span>
  </li>
</ol>

<p class="final-note">SVE FOTOKOPIJE PRILOŽENIH DOKUMENATA OVJERITI KOD NADLEŽNOG ORGANA<br><span class="final-note-alb">TË GJITHA FOTOKOPJET E DOKUMENTEVE TË BASHKANGJITURA DUHET TË VËRTETOHEN TE ORGANI KOMPETENT</span></p>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const print = (z) => printHTML(buildHtml(z));
  const word = (z) => downloadWord(buildHtml(z), `Ugostiteljska_Opstina_${(z.uo_naziv_podnosioca||'podnosilac').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="ug-opstina-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Ugostiteljska djelatnost — Opština Ulcinj</h3>
          <p className="text-sm text-slate-600 mt-1">Zahtjev Sekretarijatu za privredu Opštine Ulcinj (dvojezično CRN/ALB)</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={20} weight="bold" className="mr-2" /> Novi zahtjev
        </Button>
      </div>

      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : items.length === 0 ? (
        <Card className="p-8 text-center">
          <Storefront size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Nema zahtjeva</p>
        </Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Podnosilac</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vrsta</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>{items.map(z => (
          <tr key={z.id} className="border-b hover:bg-slate-50">
            <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja) || '-'}</td>
            <td className="px-4 py-3 font-medium">{z.uo_naziv_podnosioca || '-'}</td>
            <td className="px-4 py-3 text-xs">{z.uo_naziv_objekta || '-'}</td>
            <td className="px-4 py-3 text-xs">{(z.uo_vrsta_objekta || '-').substring(0, 20)}</td>
            <td className="px-4 py-3"><div className="flex gap-1">
              <button onClick={() => print(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600" /></button>
              <button onClick={() => word(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600" /></button>
              <button onClick={() => edit(z)} className="p-2 hover:bg-slate-200 rounded" title="Uredi"><Pencil size={16} className="text-slate-600" /></button>
              <button onClick={() => del(z.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600" /></button>
            </div></td>
          </tr>))}</tbody></table></div></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Uredi' : 'Novi zahtjev — Ugostiteljska (Opština Ulcinj)'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (v => v !== 'none' && pickClient(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Površina (m²)</Label>
                <Input value={form.uo_povrsina} onChange={e => setForm({ ...form, uo_povrsina: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs text-slate-500 uppercase">Ulica / lokacija</Label>
                <Input value={form.uo_ulica} onChange={e => setForm({ ...form, uo_ulica: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Broj (u Ulcinju)</Label>
              <Input value={form.uo_broj} onChange={e => setForm({ ...form, uo_broj: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Vrsta objekta *</Label>
              <select value={form.uo_vrsta_objekta} onChange={e => setForm({ ...form, uo_vrsta_objekta: e.target.value })} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                {VRSTE_OBJEKATA.map(v => <option key={v} value={v}>{v}</option>)}
              </select></div>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">1. Naziv/ime podnosioca *</Label>
              <Input value={form.uo_naziv_podnosioca} onChange={e => setForm({ ...form, uo_naziv_podnosioca: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">2. Adresa i matični broj</Label>
              <Input value={form.uo_adresa_maticni} onChange={e => setForm({ ...form, uo_adresa_maticni: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">3. Objekat posluje pod nazivom</Label>
              <Input value={form.uo_naziv_objekta} onChange={e => setForm({ ...form, uo_naziv_objekta: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">4. Status prostora</Label>
              <select value={form.uo_status_prostora} onChange={e => setForm({ ...form, uo_status_prostora: e.target.value })} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="vlasnistvo">Vlasništvo</option>
                <option value="zakup">Zakup</option>
                <option value="podzakup">Pod zakupu</option>
                <option value="porodica">Član porodičnog domaćinstva</option>
              </select></div>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">5. Period obavljanja djelatnosti</Label>
              <Input value={form.uo_period_djelatnosti} onChange={e => setForm({ ...form, uo_period_djelatnosti: e.target.value })} placeholder="npr. od 01.05.2026 - trajno" className="bg-white border-slate-300 text-sm" /></div>

            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Podaci za potpis:</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Ime i prezime</Label>
                  <Input value={form.uo_ime_prezime_potpisnika} onChange={e => setForm({ ...form, uo_ime_prezime_potpisnika: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Vlasnik / pravni zastupnik</Label>
                  <Input value={form.uo_vlasnik_zastupnik} onChange={e => setForm({ ...form, uo_vlasnik_zastupnik: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1 col-span-2"><Label className="text-xs text-slate-500 uppercase">Naziv firme</Label>
                  <Input value={form.uo_naziv_firme} onChange={e => setForm({ ...form, uo_naziv_firme: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
                <div className="space-y-1 col-span-2"><Label className="text-xs text-slate-500 uppercase">Adresa i telefon</Label>
                  <Input value={form.uo_adresa_telefon} onChange={e => setForm({ ...form, uo_adresa_telefon: e.target.value })} className="bg-white border-slate-300 text-sm" /></div>
              </div>
            </div>

            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Datum podnošenja</Label>
              <DateInput value={form.datum_podnosenja} onChange={e => setForm({ ...form, datum_podnosenja: e.target.value })} className="border-slate-300 text-sm" /></div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setOpen(false)}>Odustani</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white">{editingId ? 'Ažuriraj' : 'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
